import React, { useState } from 'react';
import {
  Form, Input, Button, Table, Tag, Space, Row, Col, Modal, Popconfirm, message
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, SaveOutlined
} from '@ant-design/icons';
import apiClient from 'services/apiClient';
import { useMyContext } from 'Context/MyContextProvider';
import { handleFeatureChange } from './PricingUtils';

const FeatureManagement = ({ features, setFeatures, plans, setPlans }) => {
  const { api, authToken } = useMyContext();
  const [editingFeatureId, setEditingFeatureId] = useState(null);
  const [isFeatureModalVisible, setIsFeatureModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [featureForm] = Form.useForm();

  // Feature CRUD operations
  const saveFeature = async (values) => {
    setLoading(true);
    try {
      const isEditing = !!editingFeatureId;

      const payload = {
        name: values.name,
        description: values.description,
      };

      // If editing, include the plans this feature is assigned to
      if (isEditing) {
        payload.id = editingFeatureId;
        payload.plans = plans.filter(plan =>
          plan.features.includes(editingFeatureId)
        ).map(plan => plan.id);
      }

      const config = {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      };

      let response;

      if (isEditing) {
        // Update feature
        response = await apiClient.post(`${api}plan-features/${editingFeatureId}`, payload, config);
        if (response.data.success) {
          // Update local state
          const updatedFeatures = features.map(feature =>
            feature.id === editingFeatureId ? { ...feature, ...values } : feature
          );
          setFeatures(updatedFeatures);
          message.success('Feature updated successfully');
        } else {
          message.error('Failed to update feature');
          return;
        }


      } else {
        await handleFeatureChange(api, authToken, payload, values, setFeatures, features)
      }

      setIsFeatureModalVisible(false);
      setEditingFeatureId(null);
      featureForm.resetFields();
    } catch (error) {
      console.error('Error saving feature:', error);
      message.error('Failed to save feature. Please try again.');
    } finally {
      setLoading(false);
    }
  };


  const deleteFeature = async (id) => {

    setLoading(true);
    // Call API to delete feature
    const res = await apiClient.delete(`${api}plan-features/${id}`, {
      headers: {
        'Authorization': `Bearer ${authToken}`,
      }
    });
    if (res.data.success) {
      message.success('Feature deleted successfully');
    } else {
      message.error('Failed to delete feature');
      setLoading(false);
      return;
    }
    // Remove feature from plans
    const updatedPlans = plans.map(plan => ({
      ...plan,
      features: plan.features.filter(featureId => featureId !== id)
    }));

    // Remove feature from list
    const updatedFeatures = features.filter(feature => feature.id !== id);

    setPlans(updatedPlans);
    setFeatures(updatedFeatures);
  };

  const editFeature = (record) => {
    setEditingFeatureId(record.id);
    featureForm.setFieldsValue({
      name: record.name,
      description: record.description
    });
    setIsFeatureModalVisible(true);
  };


  // Feature columns for table
  const featureColumns = [
    {
      title: '#',
      //show index column
      dataIndex: 'index',
      render: (_, __, index) => index + 1
    },
    {
      title: 'Feature Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Used In Plans',
      key: 'usedInPlans',
      render: (_, record) => {
        const usedIn = plans.filter(plan =>
          plan.features.includes(record.id)
        );
        return usedIn.map(plan => (
          <Tag color="green" key={plan.id}>
            {plan.title}
          </Tag>
        ));
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space size="small">
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => editFeature(record)}
          />
          <Popconfirm
            title="Are you sure you want to delete this feature?"
            onConfirm={() => deleteFeature(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              danger
              icon={<DeleteOutlined />}
              size="small"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            featureForm.resetFields();
            setEditingFeatureId(null);
            setIsFeatureModalVisible(true);
          }}
        >
          Add New Feature
        </Button>
      </div>
      <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
        <Table
          columns={featureColumns}
          dataSource={features}
          rowKey="id"
          bordered
          size="middle"
        />
      </div>

      {/* Feature Form Modal */}
      <Modal
        title={editingFeatureId ? "Edit Feature" : "Add New Feature"}
        open={isFeatureModalVisible}
        onCancel={() => {
          setIsFeatureModalVisible(false);
          setEditingFeatureId(null);
          featureForm.resetFields();
        }}
        footer={null}
        destroyOnClose
      >
        <Form
          form={featureForm}
          layout="vertical"
          onFinish={saveFeature}
        >
          <Form.Item
            name="name"
            label="Feature Name"
            rules={[{ required: true, message: 'Please enter the feature name' }]}
          >
            <Input placeholder="e.g., API Access" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Description"
            rules={[{ required: false, message: 'Please enter a description' }]}
          >
            <Input.TextArea placeholder="Describe what this feature provides" rows={3} />
          </Form.Item>

          <Form.Item>
            <Row gutter={8} justify="end">
              <Col>
                <Button
                  onClick={() => {
                    setIsFeatureModalVisible(false);
                    setEditingFeatureId(null);
                    featureForm.resetFields();
                  }}
                >
                  Cancel
                </Button>
              </Col>
              <Col>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={loading}
                >
                  {editingFeatureId ? "Update" : "Save"}
                </Button>
              </Col>
            </Row>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default FeatureManagement;